import { useState, useEffect } from 'react';
import Dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import useMediaQuery from '@mui/material/useMediaQuery';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button, Collapse, Grid, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterListIcon from '@mui/icons-material/FilterList';

import SelectComponent from 'components/SelectComponent';
import {
  ConvertEnumToSelectComponent,
  ConvertTextValueToSelectComponent,
  ConvertToSelectComponent,
} from 'components/SelectComponent/ConvertToSelectComponent';

import { UpdateFilter } from 'store/actions/schoolActions';

import { fetchSchoolDataFilter } from 'services/schoolService';
import { getCountriesFilter } from 'utils/countryUtils';
import { filterDefaultData } from 'utils/constants/filterConstants';

import {
  getItemBaseToSelect,
  getDefaultAccommodationOptions,
} from 'utils/filterUtils';
import {
  formatDate,
  getDaysAdded,
  getGroupedMondayOptions,
  getNextBusinessDayInRange,
  getWeekRange,
} from 'utils/date';

export const SelectGroupComponent = Dynamic(
  () => import('components/SelectGroupComponent'),
  { ssr: false }
);

const qs = require('query-string');

const Styles = {
  paper: {
    padding: (theme) => theme.spacing(2),
    textAlign: 'center',
    color: (theme) => theme.palette.text.secondary,
  },

  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
};

SchoolsFiltersComponent.propTypes = {
  getSchools: PropTypes.func.isRequired,
};

export default function SchoolsFiltersComponent({ getSchools }) {
  const countries = useSelector((state) => state.countryReducer);
  const data = useSelector((state) => state.schoolFilterDataReducer);

  const router = useRouter();
  const theme = useTheme();
  const deskBreakpoint = useMediaQuery(theme.breakpoints.up('md'));
  const dispatch = useDispatch();
  const [cities, setCities] = useState([]);
  const [toggleFilter, setToggleFilter] = useState(false);
  const [filter, setFilter] = useState({
    ...filterDefaultData,
    accommodationType: 'none',
    startDate: formatDate(
      getNextBusinessDayInRange(
        getDaysAdded(new Date(), parseInt(process.env.DAYS_BEFORE_BOOKING, 10))
      ),
      'iso-string'
    ),
    first: true,
  });

  if (
    data?.languageFilters &&
    !data.languageFilters.some((d) => d.countryId === 0)
  ) {
    const allCountries = {
      countSchools: 0,
      country: 'Todos',
      countryId: 0,
      descriptionCountry: 'Todos',
      language: 'Inglês',
      languageId: 1,
      schoolsByCity: [
        {
          city: 'Todos',
          cityId: 0,
          descriptionCity: 'Todos',
          schools: [],
          slugName: 'todos',
        },
      ],
      slugName: 'todos',
    };

    data?.languageFilters.unshift(allCountries);
  }

  useEffect(() => {
    readUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!filter.first) {
      filter.first = false;
      UpdateFilter(dispatch, filter);
      getSchools(filter);
      changeFilterData();
      updateUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const readUrl = () => {
    let routers = {
      country: 0,
      city: 0,
    };
    const routersSplit = router.asPath.split('schools')[1];
    const routerParams = routersSplit.split('?')[0];
    const params = routerParams.split('/');

    if (params.length === 3)
      routers = {
        country: params[1],
        city: params[2],
      };

    if (params.length === 2)
      routers = {
        country: params[1] || 0,
        city: 0,
      };

    let idCountry = 0;
    let idCity = 0;

    if (routers.country !== 0)
      idCountry =
        countries &&
        countries.filter((item) => item.slugName === routers.country)[0]?.id;

    if (routers.city !== 0) {
      const hasCountries = countries && countries.length;
      const countryCities =
        hasCountries &&
        countries.filter(
          (item) => item.cities.filter((c) => c.slugName === routers.city)[0]
        );
      const hasCities = countryCities && countryCities.length;

      if (hasCities) {
        idCity = countryCities[0]?.cities.filter(
          (c) => c.slugName === routers.city
        )[0].id;
      }
    }

    setCitiesByCountry(idCountry);

    setFilter({
      ...filter,
      ...router.query,
      countryId: idCountry,
      cityId: idCity,
      first: false,
    });
  };

  const updateUrl = () => {
    let countrySlugName = '';
    let citySlugName = '';

    if (filter.countryId > 0)
      countrySlugName =
        countries &&
        countries.filter(
          (item) => item.id === parseInt(filter.countryId, 10)
        )[0].slugName;

    if (filter.cityId > 0)
      citySlugName =
        countries &&
        countries
          .filter(
            (item) =>
              item.cities.filter((c) => c.id === parseInt(filter.cityId, 10))[0]
          )[0]
          .cities.filter((c) => c.id === parseInt(filter.cityId, 10))[0]
          .slugName;

    let url = `${window.location.href.split('schools')[0]}schools`;

    if (countrySlugName !== '') url = `${url}/${countrySlugName}`;
    if (citySlugName !== '') url = `${url}/${citySlugName}`;

    url = `${url}?${qs.stringify(filter, {
      skipEmptyString: true,
      skipNull: true,
    })}`;

    window.history.pushState(new Date(), '', url);
  };

  const handleChange = (name) => (value) => {
    const resetFilter = {};

    if (filter.programId === 2) {
      resetFilter.weekRange = filter.weekRange ?? 2;
    }

    switch (name) {
      case 'programId':
        resetFilter.accommodationBedroomType = '';
        resetFilter.accommodationId = 0;
        resetFilter.accommodationType = 'none';
        resetFilter.courseTypeId = 0;
        resetFilter.countryId = '0';
        resetFilter.cityId = 0;
        break;
      case 'courseTypeId':
        resetFilter.countryId = '0';
        resetFilter.cityId = 0;
        break;
      case 'accommodationType':
        if (value === 'none' || value === 'cheaper')
          resetFilter.accommodationBedroomType = value;
        break;
      default:
        break;
    }

    setFilter({
      ...filter,
      ...resetFilter,
      [name]: value,
    });
  };

  const setCountry = (id) => {
    setCitiesByCountry(id);

    setFilter({ ...filter, countryId: id, cityId: 0, updateFilter: true });
  };

  const setCitiesByCountry = (countryId) => {
    if (countryId) {
      const itens = data?.languageFilters.filter(
        (item) =>
          item.languageId === parseInt(filter.languageId, 10) &&
          item.countryId === parseInt(countryId, 10)
      );

      const Cities = [];

      itens?.map((item) => {
        item?.schoolsByCity?.map((s) =>
          Cities.push({ id: s.cityId, name: s.descriptionCity })
        );
      });

      setCities(Cities);
    }
  };

  const changeFilterData = () => {
    fetchSchoolDataFilter(dispatch, {
      courseTypeId: filter.courseTypeId,
      languageId: filter.languageId,
      countryId: filter.countryId,
      cityId: filter.cityId,
      programId: filter.programId,
      weekRange: filter.weekRange,
    });
  };

  return (
    <Paper elevation={2} sx={Styles.paper}>
      <Box ml={1} mr={1} my={1}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography
              color="primary"
              align="left"
              variant="h6"
              component="h2"
            >
              Encontre sua escola
            </Typography>
          </Grid>

          <Grid item>
            {!deskBreakpoint && (
              <Button
                onClick={() => {
                  setToggleFilter(!toggleFilter);
                }}
              >
                {toggleFilter ? <ExpandLessIcon /> : <FilterListIcon />}
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>

      <Collapse in={toggleFilter || deskBreakpoint}>
        <Box sx={Styles.container}>
          <SelectComponent
            change={handleChange('programId')}
            isDuplicateText={false}
            items={ConvertToSelectComponent(data?.programs, 'id', 'name')}
            text="Programa"
            value={filter.programId}
          />

          <SelectComponent
            change={handleChange('languageId')}
            disabled
            items={ConvertToSelectComponent(
              data?.languages,
              'id',
              'description'
            )}
            text="Idioma"
            value={filter.languageId}
          />

          <SelectComponent
            change={handleChange('courseTypeId')}
            isDuplicateText={false}
            items={ConvertToSelectComponent(
              data?.courseTypes,
              'id',
              'type',
              getItemBaseToSelect()
            )}
            text="Curso"
            value={filter.courseTypeId}
          />

          <SelectComponent
            change={(id) => setCountry(id)}
            isDuplicateText={false}
            items={ConvertToSelectComponent(
              getCountriesFilter(data?.languageFilters, filter.languageId),
              'id',
              'name'
            )}
            text="País"
            value={filter.countryId}
          />

          {parseInt(filter.countryId, 10) !== 0 && (
            <SelectComponent
              change={handleChange('cityId')}
              isDuplicateText={false}
              items={ConvertToSelectComponent(
                cities,
                'id',
                'name',
                getItemBaseToSelect(1)
              )}
              text="Cidade"
              value={filter.cityId}
            />
          )}
        </Box>

        {parseInt(filter.programId, 10) !== 2 && (
          <>
            <Box ml={1} mr={1} my={1}>
              <Typography
                align="left"
                color="primary"
                component="h2"
                variant="h6"
              >
                Simule seu orçamento
              </Typography>
            </Box>

            <Box sx={Styles.container}>
              <SelectComponent
                isDuplicateText={false}
                items={ConvertTextValueToSelectComponent(getWeekRange())}
                text="Quantidade de semanas"
                value={filter.weekRange}
                change={handleChange('weekRange')}
              />

              <SelectGroupComponent
                change={handleChange('startDate')}
                groups={getGroupedMondayOptions()}
                id="boardDate"
                name="date"
                text="Data de início"
                value={filter.startDate}
              />

              <SelectComponent
                change={handleChange('accommodationType')}
                isDuplicateText={false}
                items={ConvertEnumToSelectComponent(
                  data?.accommodations,
                  getDefaultAccommodationOptions()
                )}
                text="Acomodação"
                value={filter.accommodationType}
              />

              {filter.accommodationType.length > 3 &&
                filter.accommodationType !== 'none' &&
                filter.accommodationType !== 'cheaper' && (
                  <SelectComponent
                    change={handleChange('accommodationBedroomType')}
                    isDuplicateText={false}
                    items={ConvertEnumToSelectComponent(data?.bedroomTypes, [
                      { text: 'Menor preço', value: 'cheaper' },
                    ])}
                    text="Tipo do quarto"
                    value={filter.accommodationBedroomType}
                  />
                )}
            </Box>
          </>
        )}
      </Collapse>
    </Paper>
  );
}
